import { weaponCatalogSeed } from '@/data/weaponCatalog';
import { supabase } from '@/lib/supabase/client';
import {
  adminSetProfileBadgesRpc,
  adminUpdatePlayerPresentationRpc,
  deleteWeaponCatalogItemRpc,
} from '@/lib/supabase/rpc';
import type { Database } from '@/types/database';
import type { AppRole, ApprovalStatus, RoleCategory, WeaponCatalogItem } from '@/types/domain';
import { slugify } from '@/utils/text';

export interface ProfileAccessPayload {
  profileId: string;
  approvalStatus: ApprovalStatus;
  inGameName: string;
  notes?: string;
  roles: AppRole[];
}

export interface WeaponCatalogPayload {
  id?: string;
  filename: string;
  displayName: string;
  roleCategory: RoleCategory | null;
  imagePath: string;
  storageObjectPath?: string;
  isActive: boolean;
  notes?: string;
  uploadFile?: File | null;
}

export interface PlayerPresentationPayload {
  profileId: string;
  progressionLevelOverride?: number | null;
  chatMuted: boolean;
  chatMutedReason?: string;
}

export interface WeaponCatalogDeleteResult {
  weaponId: string;
  result: 'deleted' | 'deactivated';
  reason?: string;
  storageObjectPath?: string;
}

export interface WeaponCatalogSyncSummary {
  totalBundled: number;
  inserted: number;
  updated: number;
  unchanged: number;
  reviewCount: number;
}

export interface DiscordTestDmResult {
  profileId: string;
  status: 'sent' | 'failed' | 'skipped';
  message: string;
}

function assertSupabase() {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  return supabase;
}

async function getCurrentAccessToken(): Promise<string> {
  const client = assertSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Sign in again before sending a Discord test DM.');
  }

  return session.access_token;
}

export async function updateProfileAccess(payload: ProfileAccessPayload): Promise<void> {
  const client = assertSupabase();
  const { error } = await client.rpc('update_profile_access', {
    p_profile_id: payload.profileId,
    p_approval_status: payload.approvalStatus,
    p_in_game_name: payload.inGameName,
    p_notes: payload.notes ?? '',
    p_roles: payload.roles,
  });

  if (error) {
    throw new Error(error.message);
  }
}

export async function setGuildMembershipActive(profileId: string, isActive: boolean): Promise<void> {
  const client = assertSupabase();
  const { error } = await client.rpc('admin_set_guild_membership_active', {
    p_profile_id: profileId,
    p_guild_membership_active: isActive,
  });

  if (error) {
    throw new Error(error.message);
  }
}

export async function updatePlayerPresentation(payload: PlayerPresentationPayload): Promise<void> {
  const { error } = await adminUpdatePlayerPresentationRpc({
    profileId: payload.profileId,
    progressionLevelOverride: payload.progressionLevelOverride ?? null,
    chatMuted: payload.chatMuted,
    chatMutedReason: payload.chatMutedReason?.trim() ? payload.chatMutedReason.trim() : null,
  });

  if (error) {
    throw new Error(error.message);
  }
}

export async function replacePlayerBadges(profileId: string, badgeSlugs: string[]): Promise<void> {
  const { error } = await adminSetProfileBadgesRpc(profileId, badgeSlugs);

  if (error) {
    throw new Error(error.message);
  }
}

export async function sendDiscordTestDm(profileId?: string): Promise<DiscordTestDmResult> {
  const accessToken = await getCurrentAccessToken();
  const response = await fetch('/api/admin/test-discord-dm', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      profileId: profileId ?? null,
    }),
  });

  const payload = (await response.json().catch(() => null)) as
    | DiscordTestDmResult
    | {
        error?: string;
      }
    | null;

  if (!response.ok) {
    throw new Error(payload && typeof payload === 'object' && 'error' in payload && payload.error ? payload.error : 'Failed to send the Discord test DM.');
  }

  if (!payload || typeof payload !== 'object' || !('profileId' in payload) || !('status' in payload) || !('message' in payload)) {
    throw new Error('Unexpected Discord DM test response.');
  }

  return payload as DiscordTestDmResult;
}

type WeaponInsert = Database['public']['Tables']['weapon_catalog']['Insert'];
type WeaponUpdate = Database['public']['Tables']['weapon_catalog']['Update'];

function getFileExtension(filename: string): string {
  const segments = filename.split('.');
  return segments.length > 1 ? segments.at(-1)!.toLowerCase() : 'png';
}

async function uploadWeaponImage(file: File, displayName: string): Promise<{ imagePath: string; storageObjectPath: string; filename: string }> {
  const client = assertSupabase();
  const slug = slugify(displayName || file.name.replace(/\.[^.]+$/, ''));
  const extension = getFileExtension(file.name);
  const storageObjectPath = `${slug}-${Date.now()}.${extension}`;
  const { error } = await client.storage.from('weapon-icons').upload(storageObjectPath, file, {
    cacheControl: '3600',
    upsert: true,
  });

  if (error) {
    throw new Error(error.message);
  }

  const { data } = client.storage.from('weapon-icons').getPublicUrl(storageObjectPath);

  return {
    imagePath: data.publicUrl,
    storageObjectPath,
    filename: file.name,
  };
}

export async function saveWeaponCatalogItem(payload: WeaponCatalogPayload): Promise<void> {
  const client = assertSupabase();
  let nextImagePath = payload.imagePath.trim();
  let nextStorageObjectPath = payload.storageObjectPath ?? null;
  let nextFilename = payload.filename.trim();
  const previousStorageObjectPath = payload.storageObjectPath ?? null;

  if (payload.uploadFile) {
    const uploaded = await uploadWeaponImage(payload.uploadFile, payload.displayName);
    nextImagePath = uploaded.imagePath;
    nextStorageObjectPath = uploaded.storageObjectPath;
    nextFilename = uploaded.filename;
  }

  if (!nextImagePath) {
    throw new Error('Upload a weapon icon before saving this catalog entry.');
  }

  const baseValues: WeaponInsert & WeaponUpdate = {
    slug: slugify(payload.displayName),
    filename: nextFilename,
    display_name: payload.displayName.trim(),
    role_category: payload.roleCategory,
    image_path: nextImagePath,
    storage_object_path: nextStorageObjectPath,
    is_active: payload.isActive,
    notes: payload.notes?.trim() ? payload.notes.trim() : null,
  };

  if (payload.id) {
    const { error } = await client.from('weapon_catalog').update(baseValues).eq('id', payload.id);

    if (error) {
      throw new Error(error.message);
    }

    if (payload.uploadFile && previousStorageObjectPath && previousStorageObjectPath !== nextStorageObjectPath) {
      await client.storage.from('weapon-icons').remove([previousStorageObjectPath]);
    }

    return;
  }

  const { error } = await client.from('weapon_catalog').insert(baseValues);

  if (error) {
    throw new Error(error.message);
  }
}

export async function deleteWeaponCatalogItem(weaponId: string, confirmationText: string): Promise<WeaponCatalogDeleteResult> {
  const client = assertSupabase();
  const { data, error } = await deleteWeaponCatalogItemRpc(weaponId, confirmationText);

  if (error) {
    throw new Error(error.message);
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Unexpected weapon deletion response.');
  }

  const result = data.result === 'deactivated' ? 'deactivated' : 'deleted';
  const storageObjectPath =
    typeof data.storage_object_path === 'string' && data.storage_object_path.length > 0 ? data.storage_object_path : undefined;

  if (result === 'deleted' && storageObjectPath) {
    await client.storage.from('weapon-icons').remove([storageObjectPath]).catch(() => {
      // Best-effort cleanup. The catalog delete is already committed even if storage cleanup fails.
    });
  }

  return {
    weaponId: typeof data.weapon_id === 'string' ? data.weapon_id : weaponId,
    result,
    reason: typeof data.reason === 'string' ? data.reason : undefined,
    storageObjectPath,
  };
}

function normalizeLookup(value: string): string {
  return value.trim().toLowerCase();
}

export async function syncBundledWeaponCatalog(existingWeapons: WeaponCatalogItem[]): Promise<WeaponCatalogSyncSummary> {
  const client = assertSupabase();
  const weaponsBySlug = new Map(existingWeapons.map((weapon) => [normalizeLookup(weapon.slug), weapon]));
  const weaponsByFilename = new Map(existingWeapons.map((weapon) => [normalizeLookup(weapon.filename), weapon]));
  const weaponsByDisplayName = new Map(existingWeapons.map((weapon) => [normalizeLookup(weapon.displayName), weapon]));
  let inserted = 0;
  let updated = 0;
  let unchanged = 0;

  for (const bundledWeapon of weaponCatalogSeed) {
    const existingWeapon =
      weaponsBySlug.get(normalizeLookup(bundledWeapon.slug)) ??
      weaponsByFilename.get(normalizeLookup(bundledWeapon.filename)) ??
      weaponsByDisplayName.get(normalizeLookup(bundledWeapon.displayName)) ??
      null;

    if (!existingWeapon) {
      const { error } = await client.from('weapon_catalog').insert({
        slug: bundledWeapon.slug,
        filename: bundledWeapon.filename,
        display_name: bundledWeapon.displayName,
        role_category: bundledWeapon.roleCategory,
        image_path: bundledWeapon.imagePath,
        storage_object_path: null,
        is_active: bundledWeapon.isActive,
        notes: bundledWeapon.notes ?? null,
      });

      if (error) {
        throw new Error(error.message);
      }

      inserted += 1;
      continue;
    }

    const nextDisplayName = existingWeapon.displayName.trim() || bundledWeapon.displayName;
    const nextRoleCategory = existingWeapon.roleCategory ?? bundledWeapon.roleCategory;
    const nextImagePath = existingWeapon.storageObjectPath ? existingWeapon.imagePath : bundledWeapon.imagePath;
    const nextNotes = existingWeapon.notes?.trim() ? existingWeapon.notes : bundledWeapon.notes;
    const shouldUpdate =
      existingWeapon.filename !== bundledWeapon.filename ||
      existingWeapon.displayName !== nextDisplayName ||
      existingWeapon.roleCategory !== nextRoleCategory ||
      existingWeapon.imagePath !== nextImagePath ||
      (existingWeapon.notes ?? '') !== (nextNotes ?? '');

    if (!shouldUpdate) {
      unchanged += 1;
      continue;
    }

    const { error } = await client
      .from('weapon_catalog')
      .update({
        filename: bundledWeapon.filename,
        display_name: nextDisplayName,
        role_category: nextRoleCategory,
        image_path: nextImagePath,
        notes: nextNotes ?? null,
      })
      .eq('id', existingWeapon.id);

    if (error) {
      throw new Error(error.message);
    }

    updated += 1;
  }

  return {
    totalBundled: weaponCatalogSeed.length,
    inserted,
    updated,
    unchanged,
    reviewCount: weaponCatalogSeed.filter((weapon) => !weapon.roleCategory || !weapon.isActive).length,
  };
}
